import moment from 'moment'
import React, { useState } from 'react'

import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/entities/MasterCampaignStub'

import { calculateCampaignPosition, formatDuration, getCampaignColor, getMomentDuration } from '../utils'

export interface CampaignBarProps {
  campaign: MasterCampaignPlanCampaignBuilder
  minDate: moment.Moment
  totalMonthsToShow: number
  typeColors: Partial<Record<MasterCampaignPlanCampaignBuilderType, string>>
  hoveredType?: string | null
  isClickable?: boolean
}

const CampaignBar: React.FC<CampaignBarProps> = ({ campaign, minDate, totalMonthsToShow, typeColors, hoveredType, isClickable = false }) => {
  const [isHovered, setIsHovered] = useState(false)
  const { startDate, endDate, leftPosition, width } = calculateCampaignPosition(campaign, minDate, totalMonthsToShow)

  const intervalUnit = getMomentDuration(campaign)

  const calculateTruncatedText = (text: string, availableWidthPercent: number) => {
    const siteMinWidthValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--site-min-width')
      .replace('px', '')
    const minSiteWidth = parseInt(siteMinWidthValue, 10) || 1475
    const actualViewportWidth = window.innerWidth * 0.8
    const effectiveViewportWidth = Math.max(minSiteWidth * 0.8, actualViewportWidth)
    const chartWidth = Math.max(920, effectiveViewportWidth)
    const visibleWidth = Math.min(chartWidth, effectiveViewportWidth)
    const boxWidthInPx = (availableWidthPercent / 100) * visibleWidth
    const availableTextWidth = boxWidthInPx - 16

    const estimatedCharWidth = 7
    const maxChars = Math.floor(availableTextWidth / estimatedCharWidth)

    if (text.length <= maxChars) {
      return text
    }

    return text.substring(0, Math.max(0, maxChars - 3)) + '...'
  }

  const displayText = calculateTruncatedText(campaign.name, width)

  const getCampaignTypeName = (): string => {
    if (campaign.isOffAdCritter && campaign.offAdCritterName) {
      return campaign.offAdCritterName
    }
    return campaign.campaignType
  }

  const campaignTypeName = getCampaignTypeName()
  const isHighlighted = hoveredType === null || hoveredType === campaignTypeName
  const opacity = isHighlighted ? 1 : 0.3

  const handleClick = () => {
    if (isClickable && campaign.id) {
      const element = document.getElementById(`campaign-${campaign.id}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => isClickable && setIsHovered(true)}
      onMouseLeave={() => isClickable && setIsHovered(false)}
      style={{
        position: 'absolute',
        left: `${leftPosition}%`,
        width: `${width}%`,
        height: '28px',
        background: getCampaignColor(campaign),
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '11px',
        fontWeight: 'bold',
        padding: '0 8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: '100px',
        textAlign: 'center',
        opacity: opacity,
        transition: 'opacity 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease',
        cursor: isClickable ? 'pointer' : 'default',
        transform: isClickable && isHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isClickable && isHovered ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none'
      }}
      title={isClickable ? `${campaign.name}: ${startDate.format('MMM DD')} - ${endDate.format('MMM DD, YYYY')}. Click to view campaign details.` : `${campaign.name}: ${startDate.format('MMM DD')} - ${endDate.format('MMM DD, YYYY')}`}>
      <span>{displayText}</span>
    </div>
  )
}

export default CampaignBar
